import React from 'react';
import SEO from '../../components/SEO';
import Breadcrumb from '../../components/Breadcrumb';
import { Link } from 'react-router-dom';

const StockMarketBasics = () => {

  const stockMarketBasics = [
    {
      title: "What is the Stock Market?",
      description: "The stock market is a marketplace where shares of publicly traded companies are bought and sold.",
      keyPoints: [
        "Companies sell shares to raise capital for business expansion",
        "Investors buy shares to own a portion of the company",
        "Stock prices fluctuate based on supply and demand",
        "Stock exchanges facilitate trading (NSE, BSE in India)"
      ]
    },
    {
      title: "Types of Stocks",
      description: "Understanding different types of stocks helps in making informed investment decisions.",
      keyPoints: [
        "Large-cap stocks: Established companies with stable performance",
        "Mid-cap stocks: Medium-sized companies with growth potential",
        "Small-cap stocks: Smaller companies with high growth potential",
        "Blue-chip stocks: Large, financially sound companies"
      ]
    },
    {
      title: "How Stock Prices are Determined",
      description: "Stock prices are influenced by various factors that affect company performance and market sentiment.",
      keyPoints: [
        "Company earnings and financial performance",
        "Industry trends and economic conditions",
        "Market sentiment and investor psychology",
        "Government policies and regulations"
      ]
    },
    {
      title: "Fundamental Analysis",
      description: "Analyze a company's financial health and business prospects before investing.",
      keyPoints: [
        "Study company financial statements (P&L, Balance Sheet, Cash Flow)",
        "Evaluate key financial ratios (P/E, ROE, Debt-to-Equity)",
        "Assess company management and business model",
        "Consider industry outlook and competitive position"
      ]
    },
    {
      title: "Technical Analysis",
      description: "Use charts and patterns to predict future price movements.",
      keyPoints: [
        "Study price charts and volume patterns",
        "Identify support and resistance levels",
        "Use technical indicators (RSI, MACD, Moving Averages)",
        "Understand market trends and momentum"
      ]
    },
    {
      title: "Risk Management",
      description: "Protect your capital by managing risk effectively.",
      keyPoints: [
        "Never invest more than you can afford to lose",
        "Diversify your portfolio across different stocks",
        "Set stop-loss orders to limit losses",
        "Don't put all your money in one stock"
      ]
    }
  ];

  const tradingBasics = [
    {
      title: "Opening a Demat Account",
      description: "You need a Demat account to hold shares in electronic form."
    },
    {
      title: "Understanding Market Orders",
      description: "Learn about market orders, limit orders, and stop-loss orders."
    },
    {
      title: "Market Timings",
      description: "Indian stock markets are open from 9:15 AM to 3:30 PM (Monday to Friday)."
    },
    {
      title: "Settlement Process",
      description: "T+2 settlement means shares are delivered 2 days after the trade date."
    }
  ];

  const commonMistakes = [
    "Investing without proper research",
    "Following tips without understanding",
    "Panic selling during market downturns",
    "Overtrading and frequent buying/selling",
    "Not diversifying the portfolio",
    "Ignoring fundamental analysis",
    "Investing money you can't afford to lose"
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Stock Market Basics – Complete Guide for Beginners"
        description="Learn stock market basics for beginners. Understand how the stock market works, types of investments, and how to get started with stock trading. Complete stock market guide for India."
        keywords="stock market basics, stock trading, share market, investment guide, stock market for beginners, trading tips, NSE, BSE, stock market India"
        type="article"
        articleSection="Stock Market"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Guides', url: '/guides' },
          { name: 'Stock Market Basics', url: '/guides/stock-market-basics' }
        ]}
      />
      <Breadcrumb
        items={[
          { name: 'Home', url: '/' },
          { name: 'Stock Market Basics', url: '/guides/stock-market-basics' }
        ]}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Stock Market Basics – Complete Guide for Beginners
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Learn the fundamentals of stock market investing. This comprehensive guide will help you 
            understand how the stock market works and how to get started as a beginner investor.
          </p>
        </div>

        {/* Introduction */}
        <div className="card mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">
            Why Learn Stock Market Basics?
          </h2>
          <p className="text-gray-600 mb-4">
            The stock market offers one of the best opportunities for long-term wealth building. 
            However, investing without proper knowledge can lead to significant losses. Understanding 
            the basics helps you make informed decisions and build a successful investment portfolio.
          </p>
          <p className="text-gray-600">
            This guide covers everything you need to know to start your stock market journey with confidence.
          </p>
        </div>

        {/* Stock Market Basics */}
        <div className="space-y-8 mb-12">
          {stockMarketBasics.map((topic, index) => (
            <div key={index} className="card">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">📊</span>
                  </div>
                </div>
                <div className="flex-grow">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {topic.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {topic.description}
                  </p>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Key Points:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {topic.keyPoints.map((point, pointIndex) => (
                        <li key={pointIndex} className="flex items-start">
                          <span className="text-blue-500 mr-2">•</span>
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Trading Basics */}
        <div className="card mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">
            Getting Started with Trading
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {tradingBasics.map((basics, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">{basics.title}</h3>
                <p className="text-sm text-gray-600">{basics.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Common Mistakes */}
        <div className="card mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">
            Common Beginner Mistakes to Avoid
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {commonMistakes.map((mistake, index) => (
              <div key={index} className="flex items-start">
                <span className="text-red-500 mr-2 mt-1">⚠️</span>
                <span className="text-sm text-gray-600">{mistake}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Investment Calculator CTA */}
        <div className="card bg-gradient-to-r from-primary-50 to-blue-50 border border-primary-200">
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Plan Your Investment Strategy
            </h2>
            <p className="text-gray-600 mb-6">
              Use our investment calculators to plan your stock market investments and SIP strategy.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/calculators/sip"
                className="btn-primary text-lg px-8 py-3 rounded-xl"
              >
                SIP Calculator
              </Link>
              <Link
                to="/calculators/emi"
                className="btn-secondary text-lg px-8 py-3 rounded-xl"
              >
                EMI Calculator
              </Link>
            </div>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">
            Related Articles
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link
              to="/guides/investment-strategies"
              className="card hover:shadow-lg transition-shadow duration-200"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Investment Strategies for Wealth Building
              </h3>
              <p className="text-gray-600 text-sm">
                Learn proven investment strategies that successful investors use to build long-term wealth.
              </p>
            </Link>
            <Link
              to="/comparisons/credit-cards"
              className="card hover:shadow-lg transition-shadow duration-200"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Best Credit Cards for Investors
              </h3>
              <p className="text-gray-600 text-sm">
                Find credit cards that offer rewards and benefits for your investment and spending needs.
              </p>
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
};

export default StockMarketBasics;
